import * as Sentry from "@sentry/react";

// Get the logger instance
export const { logger } = Sentry;

// Helper function for creating custom spans
export const createSpan = (operation, name, callback, attributes = {}) => {
  return Sentry.startSpan(
    {
      op: operation,
      name: name,
    },
    (span) => {
      // Set any additional attributes
      Object.entries(attributes).forEach(([key, value]) => {
        span.setAttribute(key, value);
      });
      
      const safeCallback = typeof callback === "function" ? callback : () => {};
      return safeCallback(span);
    },
  );
};

// Helper function for UI interactions (button clicks, form submissions, etc.)
export const trackUIInteraction = (name, maybeCallbackOrAttributes, maybeAttributes = {}) => {
  const isFunction = typeof maybeCallbackOrAttributes === "function";
  const callback = isFunction ? maybeCallbackOrAttributes : () => {};
  const attributes = isFunction ? maybeAttributes : (maybeCallbackOrAttributes || {});
  return createSpan("ui.interaction", name, callback, attributes);
};

// Helper function for API calls
export const trackAPICall = (method, endpoint, callback, attributes = {}) => {
  return createSpan("http.client", `${method} ${endpoint}`, callback, {
    "http.method": method,
    "http.url": endpoint,
    ...attributes,
  });
};

// Helper function for database operations
export const trackDatabaseOperation = (operation, table, callback, attributes = {}) => {
  return createSpan("db.operation", `${operation} ${table}`, callback, {
    "db.operation": operation,
    "db.table": table,
    ...attributes,
  });
};

// Helper function for form submissions
export const trackFormSubmission = (formName, callback, attributes = {}) => {
  return createSpan("ui.form.submit", `${formName} Form Submission`, callback, {
    "form.name": formName,
    ...attributes,
  });
};

// Helper function for navigation
export const trackNavigation = (from, to, callback, attributes = {}) => {
  return createSpan("navigation", `Navigate from ${from} to ${to}`, callback, {
    "navigation.from": from,
    "navigation.to": to,
    ...attributes,
  });
};

// Helper function for error tracking with context
export const trackError = (error, context = {}) => {
  Sentry.captureException(error, {
    contexts: {
      custom: context,
    },
  });
};

// Helper function for setting user context
export const setUserContext = (user) => {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.displayName || user.username,
      // Add any other user properties you want to track
    });
  } else {
    Sentry.setUser(null);
  }
};

// Helper function for setting tags
export const setTag = (key, value) => {
  Sentry.setTag(key, value);
};

// Helper function for setting context
export const setContext = (name, data) => {
  Sentry.setContext(name, data);
};

// Helper function for breadcrumbs
export const addBreadcrumb = (message, category = "default", data = {}) => {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: "info",
  });
};

// Export Sentry instance for direct use
export { Sentry };
